import DataGrid, { DataGridConfig } from 'components/DataGrid';
import JsonToTable from 'components/JsonToTable';
import PageLayout from 'components/PageLayout';
import RepoTypeIcon from 'components/RepoTypeIcon';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import React, { useMemo } from 'react';

import Actions from './actions';
import {
  accountDataAtom,
  enabledColumnKeysAtom,
  sortOrderAtom,
  orderbyAtom,
  paginationConfigAtom,
  forceRerenderUIAtom,
  DEFAULT_ACCOUNT_LISTING_COLUMNS,
} from './state';
import HostCards from '../components/HostCards';
import UserInfoSummary from '../components/UserInfoSummary';
import { updateTabQueryParam } from '../helper';
import {
  selectedAccountIdListAtom,
  useRepositoryResetAtoms,
  filterValuesAtom,
} from '../Repositories/state';

const AccountsList = () => {
  const [paginationConfig, setPaginationConfig] = useAtom(paginationConfigAtom);
  const [orderby, setOrderBy] = useAtom(orderbyAtom);
  const [sortOrder, setSortOrder] = useAtom(sortOrderAtom); // Sort order
  const forceRerenderUI = useAtomValue(forceRerenderUIAtom);
  const [filterValues, setFilterValues] = useAtom(filterValuesAtom);

  const listingData = useAtomValue(accountDataAtom);
  const [enabledColumnKeys, updateEnabledColumnKeys] = useAtom(
    enabledColumnKeysAtom,
  );

  // the atom to update the account selection on repositories listing
  const setSelectedAccountIds = useSetAtom(selectedAccountIdListAtom);
  const resetRepositoryAtoms = useRepositoryResetAtoms();

  // NOTE: for performance we will not call this directly, and we need to use the useMemo hook
  const getAccountConfig = (): DataGridConfig => ({
    rowIdKey: 'id',
    columns: [
      {
        key: 'id',
        label: 'Id',
        sortable: true,
        headerTooltip: 'SP360 row ID',
        headerCellStyle: { width: '90px', maxWidth: '90px' },
        rowCellStyle: { width: '90px', maxWidth: '90px' },
      },
      {
        key: 'account',
        label: 'Account',
        headerTooltip: 'Title and type of the account',
        sortable: true,
        // NOTE: auto width for one column to expand automatically
        headerCellStyle: { width: 'auto' },
        rowCellStyle: { width: 'auto' },
        overrideContents: (rowData) => (
          <>
            <RepoTypeIcon repoType={rowData['host_type']} size={24} />
            &nbsp;&nbsp;{rowData.title}
          </>
        ),
      },
      {
        key: 'title',
        label: 'Account Title',
        headerTooltip: 'Title of the account',
        sortable: true,
        // NOTE: auto width for one column to expand automatically
        headerCellStyle: { width: 'auto' },
        rowCellStyle: { width: 'auto' },
      },
      {
        key: 'host_type',
        label: 'Type',
        sortable: true,
        tooltip: (rowData) => rowData['host_type'],
        headerTooltip: 'Host Type for the account',
        overrideContents: (rowData) => (
          <RepoTypeIcon repoType={rowData['host_type']} size={24} />
        ),
        headerCellStyle: { maxWidth: '50px' },
        rowCellStyle: { maxWidth: '50px' },
      },
      {
        key: 'user',
        label: 'Connected By',
        sortable: true,
        orderBy: 'user__first_name,user__last_name',
        headerTooltip: 'User who connected the repository',
        overrideContents: (rowData) =>
          `${rowData['user']['first_name']} ${rowData['user']['last_name']}`,
        tooltip: (rowData) => (
          <UserInfoSummary
            props={rowData['user']}
            description="User who connected the account in SP360."
          />
        ),
      },
      {
        key: 'is_active',
        label: 'Active',
        sortable: true,
        headerCellClassName: 'bool-column',
        headerTooltip:
          'For inactive accounts, the repositories will not be scanned.',
      },
      { key: 'created_at', label: 'Created At', sortable: true },
      { key: 'updated_at', label: 'Updated At', sortable: true },
      {
        key: 'owner_name',
        label: 'Account Owner',
        overrideContents: (rowData) => rowData.owner?.name,
        headerTooltip: 'Name of the owner, for this repo platform account .',

        rowCellClassName: 'text-nowrap',
      },
      {
        key: 'owner_company',
        label: 'Owner Company',
        overrideContents: (rowData) => rowData.owner?.company,
        headerTooltip: 'Company of the owner, for this repo platform account .',
      },
      {
        key: 'repositories',
        label: 'Repositories',
        overrideContents: (rowData) => rowData.repositories?.length,
        headerTooltip: 'Repositories/Branches connected for account..',
        rowCellStyle: { textAlign: 'center' },
        onClick: (rowData) => {
          resetRepositoryAtoms();
          setSelectedAccountIds([rowData.id]);
          setFilterValues((prev) => ({
            account_ids: [rowData.id.toString()],
          }));
          updateTabQueryParam('repo');
        },
        tooltip: (rowData) => (
          <>
            Click to view more details... <br />{' '}
            <JsonToTable data={rowData['repositories']} />
          </>
        ),
      },
      {
        key: 'actions',
        label: 'Actions',
        render: (rowData) => <Actions rowData={rowData} />,
        headerCellStyle: {
          textAlign: 'center',
          minWidth: '120px',
          maxWidth: '120px',
        },
        rowCellStyle: {
          textAlign: 'center',
          minWidth: '120px',
          maxWidth: '120px',
        },
      },
    ],
    columnsDisplayConfig: {
      enabledColumnKeys: enabledColumnKeys,
      defaultColumnKeys: DEFAULT_ACCOUNT_LISTING_COLUMNS,
      onColumnsDisplayChange: (updatedColumnKeys) => {
        updateEnabledColumnKeys(updatedColumnKeys);
      },
    },
    rowConfig: {
      classRules: [
        {
          className: 'warning-row',
          ruleFunction: (rowData) =>
            !rowData['is_active'] &&
            rowData.token_info?.access_token_failed_attempts < 10,
        },
        {
          className: 'danger-row',
          ruleFunction: (rowData) =>
            !rowData['is_active'] &&
            rowData.token_info?.access_token_failed_attempts >= 10,
        },
      ],
    },
    paginationConfig: {
      total_count: listingData?.count || 0,
      pageNumber: paginationConfig.page - 1, // page number expects a zero based index
      pageSize: paginationConfig.page_size,
      onPageChange: (newPage: number) => {
        // page number returns a zero based index, so incrementing by 1
        setPaginationConfig({
          page_size: paginationConfig.page_size,
          page: newPage + 1,
        });
      },
      onPageSizeChange: (newPageSize: number) => {
        // reset page number on page size change
        setPaginationConfig({ page_size: newPageSize, page: 1 });
      },
    },
    sortingConfig: {
      orderBy: orderby,
      order: sortOrder,
      onOrderByChange: (value: string) => setOrderBy(value),
      onOrderChange: (value) => setSortOrder(value),
    },
    // rowSelectionConfig: {
    //   preSelectedRows: selectedRowIds,
    //   onSelectionChange: (updatedRowIds) => { setSelectedRowIds(updatedRowIds) },
    // }
    //   TODO: add more configuration options for responsive columns display, for different screen sizes
    // TODO: add row selection feature
  });

  const config = useMemo<DataGridConfig>(
    () => getAccountConfig(),
    [listingData, enabledColumnKeys, forceRerenderUI, paginationConfig],
  );

  return (
    <>
      <PageLayout
        id="integrations"
        heading="New Integration"
        // description='Select your hosting platform to start scanning your code repositories.'
      >
        <HostCards />
      </PageLayout>
      <PageLayout
        id="accounts_listing"
        heading="Integrated Accounts"
        count={listingData?.count}
        description="Manage your already connected accounts. Bulk connect, activate and deactivate repositories/branches for vulnerability scanning."
      >
        <DataGrid config={config} data={listingData?.results} />
      </PageLayout>
    </>
  );
};

export default AccountsList;
